import { PortableText } from '@portabletext/react'
import { cva } from 'class-variance-authority'
import React from 'react'

import { cn } from '@/app/utils/utils'

/**
 *
 * @returns: En tekstboks-komponent, der kan bruges til at indtaste tekst.
 * @example: <Paragraph />
 * @alias: Paragraph
 * @module: components/global/Paragraph
 * @summary: Denne komponent bruges til at vise en tekstboks-komponent, der kan bruges til at indtaste tekst.
 * @see: src/components/global/Paragraph.tsx
 * @version: 1.0.0
 * @property: [size, spacing, portableText]
 
 * @author: Kasper Buchholtz
 *
**/

const ParagraphVariants = cva('font-sans first-of-type:mb-0 hyphens-auto', {
  variants: {
    size: {
      regular: 'text-regular',
      increased: 'text-increased',
      medium: 'text-medium',
    },
    spacing: {
      large: 'mb-12',
      default: 'mb-8',
      small: 'mb-4',
      none: '',
    },
  },
  defaultVariants: {
    size: 'regular',
    spacing: 'default',
  },
})

interface ParagraphProps {
  children: React.ReactNode
  size?: 'regular' | 'increased' | 'medium'
  [key: string]: any
  portableText?: boolean
  spacing?: 'default' | 'none' | 'small' | 'large'
  className?: string
}

const Paragraph: React.FC<ParagraphProps> = ({
  size,
  spacing,
  children,
  portableText,
  className,
  ...props
}) => {
  return (
    <div
      data-type="paragraph"
      {...props}
      className={cn(ParagraphVariants({ size, spacing, className }))}
    >
      {portableText ? (
        <TextComponent value={children} />
      ) : (
        <>
          <p className="whitespace-pre-line first-of-type:mb-0">{children}</p>
        </>
      )}
    </div>
  )
}

const myPortableTextComponents = {
  types: {
    myBlock: ({ children }) => <div className="my-block">{children}</div>,
    // Add your other custom block types here
  },
  marks: {
    em: ({ children }) => (
      <em className="font-semibold text-gray-600">{children}</em>
    ),
    link: ({ children, value }) => {
      const rel =
        value && value.href && !value.href.startsWith('/')
          ? 'noreferrer noopener'
          : undefined
      return (
        <a
          className="font-bold underline transition-all custom-a text-rød hover:text-purple"
          href={value.href}
          rel={rel}
        >
          {children}
        </a>
      )
    },
  },
  block: {
    // Add your custom blocks here
    normal: ({ children }) => (
      <p className="mt-2 mb-4 custom-p first-of-type:mb-0 text-regular ">
        {children}
      </p>
    ),
    h1: ({ children }) => (
      <h2 className="mt-8 mb-4 font-bold custom-h1 max-w-prose text-balance text-giant">
        {children}
      </h2>
    ),
    h2: ({ children }) => (
      <h2 className="mt-8 mb-4 font-bold custom-h2 max-w-prose text-balance text-huge">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="font-bold custom-h3 max-w-prose text-balance text-large">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="font-bold custom-h4 max-w-prose text-balance text-medium">
        {children}
      </h4>
    ),
    h5: ({ children }) => (
      <h5 className="font-bold custom-h5 max-w-prose text-balance text-increased">
        {children}
      </h5>
    ),
    h6: ({ children }) => (
      <h6 className="font-bold custom-h6 max-w-prose text-balance text-small">
        {children}
      </h6>
    ),
    ul: ({ children }) => (
      <ul className="ml-5 list-disc list-outside custom-ul">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="ml-5 list-decimal list-outside custom-ol">{children}</ol>
    ),
    strong: ({ children }) => (
      <strong className="font-bold custom-strong">{children}</strong>
    ),
    em: ({ children }) => <em className="italic custom-em">{children}</em>,
    code: ({ children }) => (
      <code className="px-1 py-0 bg-gray-200 rounded custom-code">
        {children}
      </code>
    ),
    li: ({ children }) => <li className="custom-li">{children}</li>,
    pre: ({ children }) => <pre className="custom-pre">{children}</pre>,
    sub: ({ children }) => <sub className="custom-sub">{children}</sub>,
    sup: ({ children }) => <sup className="custom-sup">{children}</sup>,
  },
  list: {
    bullet: ({ children }) => (
      <ul className="ml-5 list-disc list-outside custom-ul">{children}</ul>
    ),
    number: ({ children }) => (
      <ol className="ml-5 list-decimal list-outside custom-ol">{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => (
      <li className="list-[circle] marker:text-DabraRød mb-2">{children}</li>
    ),
    number: ({ children }) => (
      <li className="list-[decimal] marker:text-DabraRød marker:font-semibold mb-2">
        {' '}
        {children}
      </li>
    ),
  },
}

const TextComponent = (props) => {
  return (
    <PortableText
      value={props.value}
      components={myPortableTextComponents as any}
    />
  )
}

export default Paragraph
